<template>
  <component
    :is="tag"
    v-if="mounted"
    v-bind="$attrs"
    :class="classes"
    class="preview"
    :x="x"
    :y="y"
    :transform="transform"
    :width="width"
    :height="height"
    :viewBox="viewBox"
    xmlns="http://www.w3.org/2000/svg"
    @click="onFrameClick($event)"
    @drop="emitArtworkDrop"
  >
    <!-- Photographed frame, when the product was shot rather than drawn -->
    <RenderSvgPhotographedFrame
      v-if="photographed"
      class="render-svg-photographed-frame"
      :frameSpec="frameSpec"
      :maskedRendering="photographed"
      :widthInInches="baseWidthHeight.width"
      :allowUpload="allowUpload"
      :loading="Boolean(loading && loading[0])"
      :readonly="readonly"
      @upload-click="$emit('upload-click')"
    />

    <template v-else>
      <!-- Mounted artwork component -->
      <RenderSvgMountedArtwork
        class="render-svg-mounted-artwork"
        :frameSpec="frameSpec"
        :x="mouldingRearWidth"
        :y="mouldingRearWidth"
        :allowUpload="allowUpload"
        :loading="loading"
        :readonly="readonly"
        :inBundle="inBundle"
        :templatePreview="templatePreview"
        @upload-click="$emit('upload-click')"
        :moulding="moulding"
      />

      <!-- Acrylic component -->
      <RenderSvgAcrylic
        class="render-svg-acrylic"
        :frameSpec="frameSpec"
        :x="mouldingRearWidth"
        :y="mouldingRearWidth"
      />

      <!-- Moulding component -->
      <RenderSvgMoulding
        v-if="moulding"
        class="render-svg-moulding"
        :frameSpec="frameSpec"
        :moulding="moulding"
        :x="0"
        :y="0"
      />
    </template>
  </component>
</template>

<script>
import { nextTick } from 'vue';
import { getMouldingInfo } from "../../framingUtilities";
import spreeClient from "../../spree";
import RenderSvgMountedArtwork from './RenderSvgMountedArtwork.vue';
import RenderSvgAcrylic from './RenderSvgAcrylic.vue';
import RenderSvgMoulding from './RenderSvgMoulding.vue';
import RenderSvgPhotographedFrame from './RenderSvgPhotographedFrame.vue';
import { photographedSize } from '../../photographedFrameGeometry';

export default {
  components: {
    RenderSvgMountedArtwork,
    RenderSvgAcrylic,
    RenderSvgMoulding,
    RenderSvgPhotographedFrame,
  },
  props: {
    frameSpec: {
      type: Object,
      required: true
    },
    x: {
      type: Number,
      default: 0
    },
    y: {
      type: Number,
      default: 0
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '100%'
    },
    allowUpload: {
      type: Boolean,
      required: false,
      default: false
    },
    focusOn: {
      type: String,
      required: false,
      default: null
    },
    enableAnimation: {
      type: Boolean,
      required: false,
      default: false
    },
    inBundle: {
      type: Object,
      required: false,
    },
    nestedSVG: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    },
    needsMouldingReactivity: {
      type: Boolean,
      default: true
    },
    templatePreview: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click', 'upload-click', 'artwork-click', 'artwork-drop', 'mounted', 'resize'],
  provide () {
    return {
      emitArtworkClick: this.emitArtworkClick,
      emitArtworkDrop: this.emitArtworkDrop,
    }
  },
  data () {
    return {
      moulding: null,
      mounted: false,
      spree: null,
      // Animated viewBox state
      currentViewBox: null,
      _viewBoxAnimationId: null,
      _viewBoxAnimationStart: 0,
      _viewBoxAnimationDurationMs: 300
    }
  },
  async mounted () {
    this.spree = new spreeClient(this.$config && this.$config.joineryHost ? this.$config.joineryHost : "/");
    this.moulding = await getMouldingInfo(this.frameSpec.moulding.permalink, this.spree);

    // Conditionally add a watcher for frameSpec if needsMouldingReactivity is true
    // This avoids the high memory cost of watching frameSpec when not needed
    if (this.needsMouldingReactivity) {
      this._frameSpecUnwatch = this.$watch(
        'frameSpec',
        async function () {
          this.moulding = await getMouldingInfo(this.frameSpec.moulding.permalink, this.spree);
          if (
            !this.frameSpec.getArtwork().exterior.artworkOpeningWidthInInches ||
            !this.frameSpec.getArtwork().exterior.artworkOpeningHeightInInches
          ) {
            this.frameSpec.setArtworkOpeningDimensions();
          }
        },
        { deep: true }
      );
    }

    this.mounted = true;

    nextTick(() => {
      this.$emit('mounted', { ...this.baseWidthHeight });
    });
  },
  methods: {
    emitArtworkClick (artworkKey) {
      this.$emit('artwork-click', {
        frameKey: this.inBundle && this.inBundle.frameKey,
        artworkKey
      });
    },
    emitArtworkDrop (event, artworkKey) {
      this.$emit('artwork-drop', event, {
        frameKey: this.inBundle && this.inBundle.frameKey,
        artworkKey: artworkKey || 0
      });
    },
    onFrameClick (ev) {
      if (this.inBundle) {
        ev.stopPropagation();
      }

      this.emitArtworkClick(0);
    },
    _cancelViewBoxAnimation () {
      if (this._viewBoxAnimationId) {
        cancelAnimationFrame(this._viewBoxAnimationId);
        this._viewBoxAnimationId = null;
      }
      this._viewBoxAnimationStart = 0;
    },
    animateViewBoxTo (targetRect) {
      if (this.nestedSVG) return; // no viewBox on <g>
      const target = targetRect || this.baseViewBox;
      const start = this.currentViewBox || this.baseViewBox;

      const approxEqual = (a, b) => Math.abs(a - b) < 0.001;
      if (
        approxEqual(start.minX, target.minX) &&
        approxEqual(start.minY, target.minY) &&
        approxEqual(start.width, target.width) &&
        approxEqual(start.height, target.height)
      ) {
        this.currentViewBox = { ...target };
        return;
      }

      const duration = this._viewBoxAnimationDurationMs;
      const easeInOutCubic = (t) => (t < 0.5)
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const step = (timestamp) => {
        if (!this._viewBoxAnimationStart) this._viewBoxAnimationStart = timestamp;
        const elapsed = timestamp - this._viewBoxAnimationStart;
        const t = Math.min(1, elapsed / duration);
        const e = easeInOutCubic(t);

        this.currentViewBox = {
          minX: start.minX + (target.minX - start.minX) * e,
          minY: start.minY + (target.minY - start.minY) * e,
          width: start.width + (target.width - start.width) * e,
          height: start.height + (target.height - start.height) * e,
        };

        if (t < 1) {
          this._viewBoxAnimationId = requestAnimationFrame(step);
        } else {
          this._cancelViewBoxAnimation();
          this.currentViewBox = { ...target };
        }
      };

      this._viewBoxAnimationId = requestAnimationFrame(step);
    },
  },
  beforeUnmount() {
    if (this._frameSpecUnwatch) {
      this._frameSpecUnwatch();
    }
    this._cancelViewBoxAnimation();
  },
  watch: {
    // Animate when the focus alignment changes left/center/right 
    focusRect (next, prev) {
      if (!this.enableAnimation) return;
      if (!this.baseWidthHeight.width || !this.baseWidthHeight.height) return;
      this.animateViewBoxTo(next || this.baseViewBox);
    },
    // Keep currentViewBox in sync when the base canvas size changes and nothing is focused
    baseWidthHeight: {
      handler () {
        this.$emit('resize', { ...this.baseWidthHeight });
        if (this.focusRect) return;
        if (!this.baseWidthHeight.width || !this.baseWidthHeight.height) return;
        this.currentViewBox = { ...this.baseViewBox };
      },
      deep: true
    }
  },
  computed: {
    focusRect () {
      const { width, height } = this.baseWidthHeight;
      if (!width || !height) return null;
      const { rightInInches, bottomInInches, leftInInches } = this.frameSpec.getRevealOrZero();

      if (this.focusOn === 'moulding-plate') {
        const plateWidth = 2.36;
        const boxWidth = plateWidth + (this.isMobile ? 1.5 : 0.5);
        const boxHeight = this.isMobile
          ? this.mouldingRearWidth + bottomInInches - 2
          : this.mouldingRearWidth + bottomInInches + 0.5;
        const boxY = this.isMobile
          ? height - this.mouldingRearWidth - bottomInInches + 2.25
          : height - this.mouldingRearWidth - bottomInInches - 0.25;
        const boxX = (width / 2) - (boxWidth / 2);
        return { minX: boxX, minY: boxY, width: boxWidth, height: boxHeight };
      }

      if (this.focusOn === 'moulding-engraving') {
        const engravingWidth = 3;
        const boxWidth = engravingWidth + 1;
        const boxHeight = this.mouldingRearWidth;

        const boxX = (width / 2) - (boxWidth / 2);
        const boxY = height - this.mouldingRearWidth - 0.1;

        return { minX: boxX, minY: boxY, width: boxWidth, height: boxHeight };
      }

      if (this.focusOn === 'mat-caption') {
        const matCaptionWidth = 2.5;
        const boxWidth = matCaptionWidth + 0.5;
        const boxHeight = this.isMobile
          ? this.mouldingRearWidth + bottomInInches + 1
          : this.mouldingRearWidth + bottomInInches + 2;
        const boxY = this.isMobile
          ? height - this.mouldingRearWidth - bottomInInches - 1
          : height - this.mouldingRearWidth - bottomInInches - 1;
        const matCaptionAlignment = this.mat.matCaption && this.mat.matCaption.fontAlignment;
        let boxX;
        if (matCaptionAlignment === 'left') {
          boxX = this.mouldingRearWidth + leftInInches - 1;
        } else if (matCaptionAlignment === 'right') {
          boxX = width - this.mouldingRearWidth - rightInInches - boxWidth + 1;
        } else {
          boxX = (width / 2) - (boxWidth / 2);
        }
        return { minX: boxX, minY: boxY, width: boxWidth, height: boxHeight };
      }

      if (this.focusOn === 'mat-plaque') {
        const plaqueWidth = 3.75;
        const plaqueHeight = 2.25;
        const boxWidth = plaqueWidth + 0.5;
        const boxHeight = this.isMobile
          ? plaqueHeight + bottomInInches - 2.8
          : plaqueHeight + bottomInInches + 1;
        const boxY = this.isMobile
          ? height - this.mouldingRearWidth - bottomInInches + 0.2
          : height - this.mouldingRearWidth - bottomInInches - 1.5;
        const boxX = (width / 2) - (boxWidth / 2);
        return { minX: boxX, minY: boxY, width: boxWidth, height: boxHeight };
      }

      return null;
    },
    // Present only when the server found this frame a photograph it could hang, which
    // means the mask was shot square, cut out against transparency, and is of a frame the
    // shape of this one. Absent is the ordinary case and draws the frame as it always was.
    photographed() {
      return this.frameSpec && this.frameSpec.maskedRendering;
    },
    baseWidthHeight() {
      if (!this.artwork) return { width: 0, height: 0 };

      // A frame from a photo of its whole set is the size the photo says it is
      const fromSetPhoto = photographedSize(this.frameSpec);
      if (fromSetPhoto) {
        return { width: fromSetPhoto.width, height: fromSetPhoto.height };
      }

      // A photographed frame has no mats or moulding to add up, and the wall has already
      // reserved it a space from the same finished size the server measured the mask
      // against, so take that rather than rebuilding it from parts that may not exist.
      if (this.photographed && this.frameSpec.moulding && this.frameSpec.moulding.exterior) {
        const { widthInInches, heightInInches } = this.frameSpec.moulding.exterior;
        if (widthInInches && heightInInches) {
          return { width: widthInInches, height: heightInInches };
        }
      }

      const fullMat = this.frameSpec.getFullPrimaryMat();
      const matOuter = this.frameSpec.matOuterSize();
      const width = fullMat && fullMat.matWidth
        ? fullMat.matWidth + 2 * this.mouldingRearWidth
        : matOuter.width + 2 * this.mouldingRearWidth;
      const height = fullMat && fullMat.matHeight
        ? fullMat.matHeight + 2 * this.mouldingRearWidth
        : matOuter.height + 2 * this.mouldingRearWidth;
      return { width, height };
    },
    baseViewBox() {
      const { width, height } = this.baseWidthHeight;
      if (this.focusRect) return { ...this.focusRect };
      return { minX: 0, minY: 0, width, height };
    },
    tag() {
      return this.nestedSVG ? 'g' : 'svg';
    },
    classes() {
      return {
        'preview--disabled': this.disabled,
        'preview--in-bundle': this.inBundle,
        'preview--readonly': this.readonly,
      };
    },
    transform() {
      return this.nestedSVG ? `translate(${this.x}, ${this.y})` : '';
    },
    artwork() {
      return this.frameSpec.getArtwork(0);
    },
    mat() {
      return this.frameSpec.mats[0] || {};
    },
    isCanvasMoulding() {
      return this.frameSpec.isCanvasOrCanvasPaper();
    },
    faceWidth() {
      if (!this.moulding) return 0;
      // The moulding tile images for canvas mouldings include the canvas gap width in the image.
      const canvasGapWidth = this.moulding.canvas_gap_width || 0;
      return this.moulding.face_width + canvasGapWidth;
    },
    mouldingRearWidth() {
      if (this.isCanvasMoulding) {
        return this.faceWidth || 0;
      } else {
        return (this.moulding && this.moulding.rear_width) || 0;
      }
    },
    isMobile() {
      if (typeof window !== 'undefined') {
        return window.innerWidth < 832;        
      } else {
        return false;
      }
    },
    viewBox() {
      if (this.nestedSVG) return '';
      if (!this.artwork) return '0 0 0 0';
      const { width, height } = this.baseWidthHeight;

      if (this.enableAnimation && this.currentViewBox) {
        const vb = this.currentViewBox;
        return `${vb.minX} ${vb.minY} ${vb.width} ${vb.height}`;
      }
      if (this.focusRect) {
        const fr = this.focusRect;
        return `${fr.minX} ${fr.minY} ${fr.width} ${fr.height}`;
      }
      return `0 0 ${width} ${height}`;
    }
  }
};
</script>

<style lang="scss">
.preview {
  max-height: 100%;
}

.preview--disabled {
  opacity: 0.25;
}

.preview--in-bundle {
  .artwork {
    cursor: pointer;
  }
}

.preview--readonly {
  pointer-events: none;
}
</style>
